import { Collapse } from 'antd';
import React, { useEffect, useState } from 'react';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import MobileCalendarDate from '../types/MobileCalendarDate';
import { CourseInstanceCardData } from '../types/CourseInstance';
import { CourseInstanceType } from '../types/CourseInstanceType';
import { MobileOpenClassSessionSummary } from '../types/OpenClassSession';

import CourseInstanceCard from './CourseInstanceCard';
import OpenClassSessionCard from './OpenClassSessionCard';

const { Panel } = Collapse;

type CollapseProps = {
  className?: string;
  calendarDates: MobileCalendarDate[];
  onAccept: () => void;
};

const CourseInstanceCollapse: React.FC<CollapseProps> = ({
  className,
  calendarDates,
  onAccept,
}) => {
  const [confirmedCourseInstances, setConfirmedCourseInstances] = useState<
    CourseInstanceCardData[]
  >([]);
  const [potentialCourseInstances, setPotentialCourseInstances] = useState<
    CourseInstanceCardData[]
  >([]);
  const [openClassSessions, setOpenClassSessions] = useState<
    MobileOpenClassSessionSummary[]
  >([]);

  useEffect(() => {
    setConfirmedCourseInstances(
      calendarDates.flatMap((calendarDate) => calendarDate.courseInstances),
    );
    setPotentialCourseInstances(
      calendarDates.flatMap(
        (calendarDate) => calendarDate.potentialCourseInstances,
      ),
    );
    setOpenClassSessions(
      calendarDates.flatMap((calendarDate) => calendarDate.openClassSessions),
    );
  }, [calendarDates]);
  const { t } = useTranslation();

  return (
    <Collapse
      className={`bg-blueGrayLight ${className}`}
      bordered={false}
      expandIconPosition={'end'}
      expandIcon={({ isActive }) =>
        isActive ? <MinusOutlined /> : <PlusOutlined />
      }
      defaultActiveKey={[1, 2, 'open-class-sessions']}>
      {confirmedCourseInstances.length > 0 && (
        <Panel
          header={`${t(
            'components.CourseInstanceCollapse.confirmedCourseInstances',
          )}: ${confirmedCourseInstances.length}`}
          key="1">
          {confirmedCourseInstances.map((courseInstance, key) => {
            return (
              <CourseInstanceCard
                key={key}
                courseInstance={courseInstance}
                onAccept={onAccept}
                courseInstanceType={CourseInstanceType.Assigned}
              />
            );
          })}
        </Panel>
      )}
      {openClassSessions.length > 0 && (
        <Panel
          header={`${t('components.CourseInstanceCollapse.openClassSessions')}: ${openClassSessions.length}`}
          key="open-class-sessions">
          {openClassSessions.map((session) => (
            <OpenClassSessionCard key={session.id} session={session} />
          ))}
        </Panel>
      )}
      {potentialCourseInstances.length > 0 && (
        <Panel
          header={`${t(
            'components.CourseInstanceCollapse.potentialCourseInstances',
          )} ${potentialCourseInstances.length}`}
          key="2">
          {potentialCourseInstances.map((courseInstance, key) => {
            return (
              <CourseInstanceCard
                key={key}
                courseInstance={courseInstance}
                onAccept={onAccept}
                courseInstanceType={CourseInstanceType.Potential}
              />
            );
          })}
        </Panel>
      )}
    </Collapse>
  );
};

export default CourseInstanceCollapse;
